/**
 * Region types and interfaces
 * 
 * Defines the supported markets and related types
 * for region detection and currency handling.
 */

/**
 * Supported regions
 */
export type Region = 'US' | 'AU' | 'GB' | 'EU';

/**
 * Supported currencies
 */
export type Currency = 'USD' | 'AUD' | 'GBP' | 'EUR';

/**
 * Region information including currency and display details
 */
export interface RegionInfo {
  region: Region;
  currency: Currency;
  symbol: string;
  name: string;
}

/**
 * Source of region detection
 */
export type RegionDetectionSource = 
  | 'user_preference' 
  | 'app_engine_header'
  | 'gclb_header'
  | 'cloudflare_header' 
  | 'cloudflare_trace' 
  | 'ip_geolocation'
  | 'header' 
  | 'browser_locale' 
  | 'default';

/**
 * Confidence level of region detection
 */
export type RegionDetectionConfidence = 'high' | 'medium' | 'low';

/**
 * Result of region detection with metadata
 * 
 * The backend returns the actual country code detected, and the frontend maps
 * it to an active market for product display.
 */
export interface RegionDetectionResult {
  /** ISO 3166-1 alpha-2 country code (e.g., "AU", "US", "GB", "CA") */
  countryCode: string;
  /** Mapped region for product display */
  region: Region;
  source: RegionDetectionSource;
  confidence: RegionDetectionConfidence;
  detectedAt?: Date;
}

/**
 * Region configuration mapping
 */
export const REGION_CONFIG: Record<Region, RegionInfo> = {
  US: {
    region: 'US',
    currency: 'USD',
    symbol: '$',
    name: 'United States',
  },
  AU: {
    region: 'AU',
    currency: 'AUD',
    symbol: 'A$',
    name: 'Australia',
  },
  GB: {
    region: 'GB',
    currency: 'GBP',
    symbol: '£',
    name: 'United Kingdom',
  },
  EU: {
    region: 'EU',
    currency: 'EUR',
    symbol: '€',
    name: 'European Union',
  },
} as const;

/**
 * Default region (US)
 */
export const DEFAULT_REGION: Region = 'US';

/**
 * Supported region codes
 */
export const SUPPORTED_REGIONS: readonly Region[] = ['US', 'AU', 'GB', 'EU'] as const;

/**
 * ISO country code to Region mapping
 * Maps common ISO 3166-1 alpha-2 country codes to our Region type
 */
export const COUNTRY_CODE_TO_REGION: Record<string, Region> = {
  US: 'US',
  AU: 'AU',
  GB: 'GB',
} as const;
